import React, { FC, useCallback, useMemo } from "react";

import { faArrowDownWideShort, faArrowUpWideShort } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { omit } from "lodash-es";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import User from "@mapmycustomers/shared/types/User";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import { Button } from "@mapmycustomers/ui";

import CreatorFilter from "@app/component/CreatorFilter/CreatorFilter";
import ModifiedInPastFilter from "@app/component/filtering/components/ModifiedInPastFilter";
import mergeFilterModels from "@app/component/view/utils/mergeFilterModels";
import noteFieldModel, { NoteFieldName } from "@app/util/fieldModel/NoteFieldModel";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import styles from "./Filters.module.scss";

export interface FiltersProps {
  filter?: FilterModel;
  onFetchNotes?: (payload?: { filter?: FilterModel; sort?: SortModel }) => void;
  sort: SortModel;
}

const Filters: FC<FiltersProps> = ({ filter, onFetchNotes, sort }) => {
  const sortOrder = sort[0]?.order ?? SortOrder.ASC;

  const handleSortChange = useCallback(() => {
    const field = noteFieldModel.getByName(NoteFieldName.UPDATED_AT);

    if (field) {
      onFetchNotes?.({
        sort: [{ field, order: sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC }],
      });
    }
  }, [onFetchNotes, sortOrder]);

  const handleCreatedByChange = useCallback(
    (userIds: User["id"][]) => {
      onFetchNotes?.({
        filter: userIds.length
          ? {
              ...filter,
              [NoteFieldName.CREATOR]: { operator: FilterOperator.CONTAINS, value: userIds },
            }
          : omit(filter, NoteFieldName.CREATOR),
      });
    },
    [filter, onFetchNotes]
  );

  const handleUpdateFilter = useCallback(
    (filterModel: Partial<FilterModel>) => {
      const resultingFilterModel = mergeFilterModels(filter ?? {}, filterModel);

      onFetchNotes?.({
        filter: resultingFilterModel,
      });
    },
    [filter, onFetchNotes]
  );

  const selectedUserIds: User["id"][] = useMemo(() => {
    const condition = filter?.[NoteFieldName.CREATOR];
    if (
      !condition ||
      !isSimpleCondition(condition) ||
      condition.operator !== FilterOperator.CONTAINS
    ) {
      return [];
    }

    return condition.value;
  }, [filter]);

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <CreatorFilter onChange={handleCreatedByChange} selectedUserIds={selectedUserIds} />
        <ModifiedInPastFilter filter={filter} onUpdateFilterModel={handleUpdateFilter} />
      </div>
      <div>
        <Button
          icon={
            <FontAwesomeIcon
              icon={sortOrder === SortOrder.ASC ? faArrowUpWideShort : faArrowDownWideShort}
            />
          }
          iconOnly
          onClick={handleSortChange}
        />
      </div>
    </div>
  );
};

export default Filters;
